import type { QueuedEditOutcome, QueuedTurnWrites } from '@/data/code-sessions/queue-mutations';
import type { TurnActivity } from '@/data/code-sessions/turn-activity';
import type {
  CodeQueuedTurnEdit,
  CodeSessionTurn,
  CodeTurnDelivery,
  PendingPrompt,
} from '@/data/code-sessions/types';
import type { CodeSessionEvent } from '@/lib/code-sessions/types';

import type { ComposerDraft } from './code-composer-draft';
import type { PendingSteer } from './transcript-model';

import { turnPromptAttachments } from '@/data/code-sessions/attachments';
import { turnDisplayText } from '@/data/code-sessions/turn-prompt';
import { editablePrompt, isChangeableTurn, isPinnedTurn } from '@/lib/code-sessions/turn-queue';
import { deterministicPromptId, turnPromptId } from '@/lib/code-sessions/turn-utils';

/** What an edit of a queued prompt opens in the composer. */
export type QueuedPromptEditSource = {
  /** The author's words, not the row's label, with the skills bound to them and the saved files. */
  draft: ComposerDraft;
  /** The prompt sends file comments or quotes after the words, which the edit keeps unseen. */
  keepsContext: boolean;
};

/**
 * A queued prompt as the tray lists it. Its `hold` says what the tray may do with it, decided once
 * from what this tab knows of it (`queuedRow`), and only a movable prompt may have an `edit`.
 */
export type CodeQueuedPrompt = {
  id: string;
  text: string;
  /**
   * A steer waits for the agent's next step in the transcript; the tray lists
   * only queued prompts, which offer Steer.
   */
  delivery: CodeTurnDelivery;
  /** How many files the prompt carries; absent when none. */
  attachmentCount?: number;
} & (
  | {
      /**
       * Anyone who may write to the session may move the prompt among the other movable rows: a
       * follow-up no send has claimed (`isChangeableTurn`), whoever wrote it. The server moves
       * only those, so a move never names another row.
       */
      hold: 'movable';
      /**
       * Present only when the viewer may edit the prompt: their own follow-up, in words of their
       * own (`editablePrompt`).
       */
      edit?: QueuedPromptEditSource;
    }
  | {
      /**
       * - `pinned`: a send claimed the prompt while it still reads queued, as after a send whose
       *   outcome was unclear (`isPinnedTurn`): it can no longer change, though Steer and Remove
       *   still work.
       * - `saving`: only this tab knows of the prompt: no saved turn has it yet. It has no edit
       *   until its saved turn arrives.
       * - `fixed`: the tray offers no change: no one may change the prompt, as a check-in or
       *   another agent's message, or this tab cannot tell yet, as for a turn it knows only from
       *   another tab's events until its saved turn loads.
       */
      hold: 'pinned' | 'saving' | 'fixed';
      edit?: never;
    }
);

/**
 * The composer's tray: the prompts waiting for the agent, and Steer, Remove, Edit and Move for
 * them. Steer, Remove and Move report their own failures, so the tray only starts them.
 */
export type ComposerQueueModel = {
  /** Queued follow-ups, in the order they run. */
  rows: readonly CodeQueuedPrompt[];
  /** The rows whose Steer request is in flight. */
  steeringTurnIds: readonly string[];
  /** Absent while the viewer can't make the session's agent run, so the tray offers no Steer. */
  steer?: (turnId: string) => void;
  remove: (turnId: string) => void;
  /** Save an edit of a row whose `edit` allows one; resolves with how it ended, never rejects. */
  saveEdit: (turnId: string, edit: CodeQueuedTurnEdit) => Promise<QueuedEditOutcome>;
  /**
   * Put a `movable` row just before another movable row, or last when `beforeTurnId` is null. The
   * order changes when the server answers; resolves once the session holds it, never rejects.
   */
  move: (turnId: string, beforeTurnId: string | null) => Promise<void>;
  /**
   * A move or a steer is in flight (`composerQueueModel`). The tray takes one action at a time, as
   * T3 Code's does: while it is busy, its rows take no other and its shortcuts send nothing. An
   * open edit's Cancel and Save still work.
   */
  busy: boolean;
};

/**
 * The tray's model, from the session's rows, its Steer and Remove, and its edits and moves. A
 * Remove takes its row out of the tray at once, so only a move or a steer keeps the tray busy.
 */
export function composerQueueModel({
  writes,
  ...queue
}: Pick<ComposerQueueModel, 'rows' | 'steeringTurnIds' | 'steer' | 'remove'> & {
  writes: QueuedTurnWrites;
}): ComposerQueueModel {
  return {
    ...queue,
    saveEdit: writes.saveEdit,
    move: writes.move,
    busy: writes.moving || queue.steeringTurnIds.length > 0,
  };
}

type TurnEvent = Extract<CodeSessionEvent, { kind: 'turn' }>;

export type WaitingPromptsInput = {
  /** The session, whose attachment route serves its saved turns' files. */
  sessionId: string;
  /** Who views the session: only a follow-up's author may edit it. */
  viewerProfileId: string | undefined;
  /**
   * Whether the session's queue takes an edit or a move (`SessionCapabilities.queuedEdits`). One
   * on the person's own computer queues in OpenCode's inbox, which keeps a prompt as sent: Steer
   * and Remove work, an edit or a move does not, and no send is about to claim the row.
   */
  queuedEdits: boolean;
  /** The session's saved turns. */
  turns: readonly Pick<
    CodeSessionTurn,
    | 'id'
    | 'index'
    | 'status'
    | 'inboxId'
    | 'admissionAttemptedAt'
    | 'delivery'
    | 'prompt'
    | 'authoredText'
    | 'skills'
    | 'attachments'
    | 'promptAttachments'
    | 'discardedAt'
    | 'checkInReason'
    | 'messageFrom'
    | 'authorProfileId'
  >[];
  /** The transcript's saved user messages' text, by message ID. */
  savedUserText: ReadonlyMap<string, string>;
  /** In seq order. */
  turnEvents: readonly TurnEvent[];
  /** Each turn's activity (`deriveTurnActivity`), which decides which turns still wait. */
  activity: readonly Pick<TurnActivity, 'id' | 'status'>[];
  /** This tab's sends whose content it keeps until the session's saved content has it. */
  pendingPrompts: readonly Pick<
    PendingPrompt,
    'clientId' | 'turnId' | 'text' | 'delivery' | 'attachments'
  >[];
  /** Turns a Remove request is taking out of the queue. */
  cancellingTurnIds: readonly string[];
  /** Steers this tab asked to cut in. */
  cuttingInTurnIds: readonly string[];
};

export type WaitingPrompts = {
  /** The tray's rows: queued follow-ups in queue order. */
  rows: CodeQueuedPrompt[];
  /** Steers the agent has not taken yet, by turn ID, which the transcript shows dimmed. */
  pendingSteers: Map<string, PendingSteer>;
  /** The user bubbles the transcript leaves out, by message ID: the queued follow-ups'. */
  queuedMessageIds: Set<string>;
};

/**
 * The prompts waiting for the agent: queued follow-ups for the tray, and steers for the
 * transcript. A turn waits while its activity reads `queued`, which already folds in its saved
 * row, its turn events, and OpenCode's inbox deliveries and cancellations. Saved turns and this
 * tab's own sends give each one's text and files; turn events give only the place of a turn the
 * session's snapshot does not hold yet. Remove requests in flight take theirs out.
 */
export function deriveWaitingPrompts(input: WaitingPromptsInput): WaitingPrompts {
  // Queue rows are keyed by cubic turn ID so cancel/steer hit /turns/{id}.
  // OpenCode inbox items join back to their turn through inboxId.
  const turnById = new Map(input.turns.map((turn) => [turn.id, turn]));
  const promptIdOf = (turnId: string) => {
    const turn = turnById.get(turnId);
    return turn ? turnPromptId(turn) : deterministicPromptId(turnId);
  };
  // A saved turn keeps its saved place: an older event never undoes a move.
  const eventIndex = new Map<string, number>();
  for (const event of input.turnEvents) {
    if (!turnById.has(event.turnId)) {
      eventIndex.set(event.turnId, event.index);
    }
  }
  const sentOrder = (turnId: string) =>
    input.pendingPrompts.findIndex((prompt) => prompt.turnId === turnId);
  // This tab's sends that nothing else places yet go last, in the order it sent them.
  const place = (turnId: string) =>
    turnById.get(turnId)?.index ?? eventIndex.get(turnId) ?? Number.MAX_SAFE_INTEGER;
  // An idle send is optimistic work, even while its POST still says queued
  // or admitted. Use the same activity decision as the transcript timer.
  const waiting: WaitingTurn[] = input.activity
    .filter(
      (turn) =>
        turn.status === 'queued' &&
        turnById.get(turn.id)?.discardedAt == null &&
        // Activity reads the transcript only through saved turns, and a saved message can
        // confirm this tab's send before its saved turn arrives.
        !input.savedUserText.has(promptIdOf(turn.id)),
    )
    .map(({ id }) => {
      const saved = turnById.get(id);
      const pending = input.pendingPrompts.find((prompt) => prompt.turnId === id);
      // A turn known only from another tab's events offers Steer until its row loads.
      return { id, saved, pending, delivery: saved?.delivery ?? pending?.delivery ?? 'queue' };
    })
    .sort((a, b) => place(a.id) - place(b.id) || sentOrder(a.id) - sentOrder(b.id));

  const removing = (turnId: string) => input.cancellingTurnIds.includes(turnId);
  // A queued follow-up is future work and waits in the tray. A steer is
  // part of the conversation now: the transcript shows it, dimmed, until
  // the agent takes it.
  const queued = waiting.filter((turn) => turn.delivery === 'queue');
  return {
    rows: queued.filter((turn) => !removing(turn.id)).map((turn) => queuedRow(turn, input)),
    pendingSteers: new Map<string, PendingSteer>(
      waiting
        .filter((turn) => turn.delivery === 'steer' && !removing(turn.id))
        .map(({ id }) => [
          id,
          { turnId: id, kind: input.cuttingInTurnIds.includes(id) ? 'interrupting' : 'waiting' },
        ]),
    ),
    // Keep the content and submission time for reconciliation, but show it in
    // only one place. Cancellation hides the tray row without flashing a bubble.
    queuedMessageIds: new Set(
      queued.flatMap(({ id }) => [id, promptIdOf(id), deterministicPromptId(id)]),
    ),
  };
}

type SavedTurn = WaitingPromptsInput['turns'][number];

/** A turn that waits for the agent, with what this tab knows of it. */
type WaitingTurn = {
  id: string;
  saved: SavedTurn | undefined;
  pending: WaitingPromptsInput['pendingPrompts'][number] | undefined;
  delivery: CodeTurnDelivery;
};

function queuedRow(
  { id, saved, pending }: WaitingTurn,
  {
    sessionId,
    viewerProfileId,
    queuedEdits,
  }: Pick<WaitingPromptsInput, 'sessionId' | 'viewerProfileId' | 'queuedEdits'>,
): CodeQueuedPrompt {
  const attachmentCount =
    pending?.attachments.length ??
    (saved === undefined ? 0 : turnPromptAttachments(sessionId, saved).length);
  const row = {
    id,
    text: pending?.text ?? (saved ? turnDisplayText(saved) : 'Queued follow-up'),
    delivery: 'queue' as const,
    ...(attachmentCount > 0 ? { attachmentCount } : {}),
  };
  if (saved === undefined) {
    return { ...row, hold: pending === undefined ? 'fixed' : 'saving' };
  }
  if (!queuedEdits) {
    return { ...row, hold: 'fixed' };
  }
  if (!isChangeableTurn(saved)) {
    return { ...row, hold: isPinnedTurn(saved) ? 'pinned' : 'fixed' };
  }
  const edit =
    viewerProfileId !== undefined && saved.authorProfileId === viewerProfileId
      ? editSource(sessionId, saved)
      : undefined;
  return { ...row, hold: 'movable', ...(edit ? { edit } : {}) };
}

/** What an edit of `turn`, which can still change, opens with; undefined when cubic worded it. */
function editSource(sessionId: string, turn: SavedTurn): QueuedPromptEditSource | undefined {
  const words = editablePrompt(turn);
  if (words === null) {
    return undefined;
  }
  return {
    draft: {
      text: words.text,
      skills: turn.skills ?? [],
      attachments: turnPromptAttachments(sessionId, turn),
    },
    keepsContext: words.context !== '',
  };
}
